import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Button, Spinner, Container, Row, Col } from 'react-bootstrap'
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min'
import { PERIOD_MONTH, PERIOD_YEAR, PLANS, PLANS_ID, ALL_FEATURES, PLAN_RANK } from '../../../constants/plans'
import { CURRENT_PLAN, DOWNGRADE, UPGRADE } from '../UpgradePayModal'
import DowngradeChecklist from './DowngradeChecklist'
import PaymentSummary from '../../PaymentModal/components/PaymentSummary'
import PlanFeatures from '../../PaymentModal/components/PlanFeatures'
import styles from '../../PaymentModal/PaymentModal.module.css'
import { formatFileSize } from 'utils/general'
import { accountModal, showUpgradeModal } from 'Manage/actions'
import { isIndia } from '../../../constants/currency'

const downGradeFeatureList = (currentPlan, newPlan) => {
  const currentPlanFeatures = ALL_FEATURES.filter((feature) => feature.enabled.includes(currentPlan?.id))
  const newPlanFeatures = ALL_FEATURES.filter((feature) => feature.enabled.includes(newPlan?.id))
  return currentPlanFeatures.filter(
    (currentFeature) => !newPlanFeatures.some((newFeature) => newFeature.label === currentFeature.label)
  )
}

const getPriceLabel = (plan, period) =>
  plan.prices[period] && `${plan.prices[period].symbol || '$'} ${plan.prices[period].value}`

const checkPlanChange = (currentPlan, newPlan) => {
  if (PLAN_RANK[currentPlan] < PLAN_RANK[newPlan]) {
    return UPGRADE
  } else if (PLAN_RANK[currentPlan] > PLAN_RANK[newPlan]) {
    return DOWNGRADE
  } else {
    return CURRENT_PLAN
  }
}

const PlanView = ({
  isLoading,
  currentPlanId,
  periodProp,
  onConfirm,
  onDowngradeSubscription,
  interval,
  selectedPlan,
  userProfile,
  liveSites,
  customDomains,
  dispatch,
  handleClose
}) => {
  const [period, setPeriod] = useState(periodProp)
  const [planStage, setPlanStage] = useState(CURRENT_PLAN)
  const [downGradeList, setDownGradeList] = useState([])
  const [isDowngradeCheckList, setIsDowngradeCheckList] = useState(false)
  const [downgradeIssues, setDowngradeIssues] = useState({})

  const activeInterval = interval === 'year' ? PERIOD_YEAR : PERIOD_MONTH
  const isYearlyToMonthly = interval === 'year' && period === PERIOD_MONTH
  const isSamePlan = selectedPlan.id === currentPlanId && activeInterval === period
  const isDisabled = isYearlyToMonthly || isSamePlan
  const isStripeCustomer = `${userProfile?.customerId}`?.startsWith('cus_')
  const isPaddleBillingCustomer = `${userProfile?.customerId}`?.startsWith('ctm_')
  const isEligibleForAutomatedDowngrade = isStripeCustomer || isPaddleBillingCustomer

  const handleComparePlans = () => {
    handleClose()
    dispatch(accountModal(false))
    dispatch(showUpgradeModal(true))
  }

  const getCurrentPlanObject = () => {
    return { id: currentPlanId }
  }

  useEffect(() => {
    setPlanStage(checkPlanChange(currentPlanId, selectedPlan.id))
    const currentPlanObject = getCurrentPlanObject()
    setDownGradeList(downGradeFeatureList(currentPlanObject, selectedPlan))
  }, [selectedPlan])

  useEffect(() => {
    // Force annual pricing for Indian users
    if (isIndia) {
      setPeriod(PERIOD_YEAR)
    } else if (interval) {
      setPeriod(activeInterval)
    }
  }, [interval])

  const checkDowngradeEligibility = (selectedPlan, userProfile, liveSites) => {
    const issues = {
      projectsToArchive: [],
      customDomains: [],
      selectedPlan
    }

    let downgradeEligible = true
    const tiiny_plan = selectedPlan.label === 'Tiny'
    // Check project limits
    const allowedProjectCount = selectedPlan.projectLimit
    const fileSizeLimit = selectedPlan.fileSizeLimit * 1024 * 1024

    // Identify over-sized projects with customDomain
    const oversizedProjects = liveSites.filter((site) => site.size > fileSizeLimit)
    const sortedProjects = liveSites.sort((a, b) => a.created - b.created)

    // Identify projects with customDomain
    const projectsWithCustomDomains = tiiny_plan
      ? liveSites.filter((project) => project.isCustomDomain && project.email === userProfile.email)
      : []
    // Identify projects with password protection or email gate enabled
    const restrictedProjects = tiiny_plan
      ? liveSites.filter((site) => site.passwordProtected || site.enableEmailGate)
      : []

    // Filter out the projects that are oversized, have custom domains, or are restricted
    const filteredProjects = liveSites.filter(
      (project) =>
        !oversizedProjects.includes(project) &&
        !projectsWithCustomDomains.includes(project) &&
        !restrictedProjects.includes(project)
    )

    // Get the projects to keep
    const projectsToKeep = filteredProjects.slice(0, allowedProjectCount)
    const projectsToArchive = Array.from(
      new Set([
        ...oversizedProjects,
        ...projectsWithCustomDomains,
        ...restrictedProjects,
        ...sortedProjects.filter((project) => !projectsToKeep.includes(project))
      ])
    )

    if (projectsToArchive.length > 0) {
      downgradeEligible = false
      issues.projectsToArchive.push({
        projects: projectsToArchive.map((site) => ({
          subdomain: site.subdomain,
          fileSize: formatFileSize(site.size)
        })),
        allowedProjectCount
      })
    }

    // Check custom domains
    if (tiiny_plan) {
      if (customDomains.length > 0) {
        downgradeEligible = false
        issues.customDomains.push({
          projectsWithCustomDomains: customDomains.map((project) => project.domain)
        })
      }
    }

    return downgradeEligible ? { eligible: true, issues: {} } : { eligible: false, issues }
  }

  const onSubmitDownGrade = (selectedPlan, period) => {
    const planPrices = {
      priceId: selectedPlan?.prices?.[period]?.stripe?.priceId,
      paddleBillingId: selectedPlan?.prices?.[period]?.paddle?.priceId,
      payProId: selectedPlan?.prices?.[period]?.payPro?.priceId,
      productId: selectedPlan?.id
    }
    onDowngradeSubscription(selectedPlan, planPrices, period)
  }

  const onSubmit = (selectedPlan, period) => {
    if (planStage === DOWNGRADE) {
      if (isEligibleForAutomatedDowngrade) {
        const result = checkDowngradeEligibility(selectedPlan, userProfile, liveSites)
        if (!result.eligible) {
          setDowngradeIssues(result.issues)
          setIsDowngradeCheckList(true) // show checklist
        } else onSubmitDownGrade(selectedPlan, period)
      } else {
        onSubmitDownGrade(selectedPlan, period)
      }
    } else {
      onConfirm({ plan: `${getPriceLabel(selectedPlan, period)} ${period}`, period })
    }
  }

  return (
    <>
      {isDowngradeCheckList ? (
        <DowngradeChecklist
          downgradeIssues={downgradeIssues}
          onSubmitDownGrade={onSubmitDownGrade}
          period={period}
          isLoading={isLoading}
        />
      ) : (
        <Container fluid className="p-0">
          <Row className="m-0">
            <PlanFeatures
              plan={selectedPlan}
              onClose={handleClose}
              handleComparePlans={handleComparePlans}
              stage={planStage}
              list={downGradeList}
              currentPlan={PLANS_ID[currentPlanId]}
            />
            <Col lg={7} className={`p-4 ${styles.paymentContainer}`}>
              <div>
                <span className="text-dark-blue font-24 text-700">Choose a payment plan</span>
                <img
                  alt="delete"
                  onClick={handleClose}
                  className="link float-right d-none d-lg-block"
                  src="/delete.png"
                  height="20"
                />
              </div>
              <div className="position-relative">
                <PaymentSummary
                  plan={selectedPlan}
                  period={period}
                  onPeriodChange={setPeriod}
                  isLoading={isLoading}
                  isError={false}
                  showExtra={false}
                  currentCurrency={selectedPlan.prices[period]?.symbol || '$'}
                  basePrice={0}
                  extraTeamMembers={0}
                  extraTeamMembersCost={0}
                  total={0}
                  showCurrencyDropdown={false}
                  onCurrencySelected={() => {}}
                  discountPercent={0}
                  hideTotals={true}
                  hideGuarantee={true}
                  isIndia={isIndia}
                />
                {!isSamePlan && !isYearlyToMonthly && planStage === UPGRADE && (
                  <p className={`mt-3 mb-0 ${styles.upgradeExplanation}`}>
                    {period === PERIOD_YEAR ? (
                      <>
                        You&apos;ll only pay the <strong>price difference</strong> between your current annual plan and
                        the upgraded plan today. At your next renewal, you&apos;ll be charged the{' '}
                        <strong>full annual price</strong> of the upgraded plan.
                      </>
                    ) : (
                      <>
                        You&apos;ll only pay the <strong>price difference</strong> between your current plan and the
                        upgraded plan today. At your next renewal, you&apos;ll be charged the{' '}
                        <strong>full monthly price</strong> of the upgraded plan.
                      </>
                    )}
                  </p>
                )}
                {isSamePlan && activeInterval === period && (
                  <p className={`bold mt-3 ${styles.currentPlanText}`}>This is your current plan.</p>
                )}
                {isYearlyToMonthly && (
                  <p className={`op-6 mt-3 ${styles.intervalWarning}`}>
                    *Interval changes are only allowed at the end of the current billing period*
                  </p>
                )}
                {planStage === DOWNGRADE && !isSamePlan && !isYearlyToMonthly && (
                  <p className={`mt-3 ${styles.downgradeExplanation}`}>
                    If you choose to downgrade, your account will remain on the current plan until your next billing
                    cycle.
                  </p>
                )}
                <div className="mt-4">
                  <Button
                    className={`w-100 py-2 ${styles.upgradeButton}`}
                    variant="primary"
                    type="submit"
                    disabled={isLoading || isDisabled}
                    onClick={() => onSubmit(selectedPlan, period)}
                  >
                    {planStage === DOWNGRADE ? 'Downgrade' : 'Upgrade now'}
                    {isLoading && <Spinner className="ms-2" animation="border" size="sm" />}
                  </Button>
                </div>
                <div className="text-center mt-3">
                  <a
                    className={`text-darker-blue line-height-16 text-500 ${styles.guaranteeLink}`}
                    target="_blank"
                    href="https://helpdesk.tiiny.host/en/article/how-does-your-7-day-money-back-guarantee-work-ghtpu0/"
                    rel="noreferrer"
                  >
                    <img alt="dollar" src="/assets/icons/currency-dollar.svg" className="mb-1" height={16} />
                    &nbsp;7-day money-back guarantee
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </>
  )
}

const mapStateToProps = ({ manage }) => ({
  userProfile: manage.userProfile,
  liveSites: manage.liveSites,
  customDomains: manage.customDomains
})

const mapDispatchToProps = (dispatch) => ({ dispatch })

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PlanView))
